import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { BodyPortal } from '../body-portal';

export interface DateRangeValue {
  from: string;
  to: string;
}

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
}

type PickerMode = 'days' | 'years';

const DROPDOWN_WIDTH = 280;
const DROPDOWN_GAP = 6;
const DROPDOWN_ESTIMATED_HEIGHT = 320;
const VIEWPORT_MARGIN = 8;

const MONTH_NAMES = [
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function toIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function fromIso(value: string): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function formatDisplay(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
}

function buildCalendarDays(year: number, month: number): CalendarDay[] {
  const today = new Date();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days: CalendarDay[] = [];
  const startDate = new Date(year, month, 1 - startOffset);

  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    days.push({
      date,
      inMonth: date.getMonth() === month,
      isToday: isSameDay(date, today),
    });
  }

  return days;
}

@Component({
  selector: 'tc-date-range-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.css',
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  private readonly elRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly vcr = inject(ViewContainerRef);
  private readonly portal = new BodyPortal(this.vcr, this.renderer, this.document);
  private readonly onScrollCapture = () => {
    if (this.open()) {
      this.updatePosition();
    }
  };

  @ViewChild('dropdownTpl', { read: TemplateRef })
  private dropdownTpl!: TemplateRef<unknown>;

  readonly dayLabels = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  from = input<string>('');
  to = input<string>('');
  ariaLabel = input<string>('Výběr období');
  rangeChange = output<DateRangeValue>();

  readonly open = signal(false);
  readonly mode = signal<PickerMode>('days');
  readonly dropdownTop = signal<number | null>(null);
  readonly dropdownBottom = signal<number | null>(null);
  readonly dropdownLeft = signal<number | null>(null);
  private readonly _viewYear = signal(new Date().getFullYear());
  private readonly _viewMonth = signal(new Date().getMonth());
  private readonly _yearsBase = signal(new Date().getFullYear());
  private readonly _draftStart = signal<Date | null>(null);
  private readonly _draftEnd = signal<Date | null>(null);
  private readonly _pickingEnd = signal(false);
  private readonly _hoverDate = signal<Date | null>(null);

  constructor() {
    effect(() => {
      const from = fromIso(this.from());
      const to = fromIso(this.to());

      if (!this.open()) {
        untracked(() => {
          this._draftStart.set(from);
          this._draftEnd.set(to);
          this._pickingEnd.set(false);
          this._hoverDate.set(null);

          const anchor = from ?? to ?? new Date();
          this._viewYear.set(anchor.getFullYear());
          this._viewMonth.set(anchor.getMonth());
          this._yearsBase.set(anchor.getFullYear());
        });
      }
    });
  }

  readonly displayRange = computed(() => {
    const from = fromIso(this.from());
    const to = fromIso(this.to());

    if (from && to) {
      return `${formatDisplay(from)} – ${formatDisplay(to)}`;
    }

    if (from) {
      return formatDisplay(from);
    }

    return '— nevybráno —';
  });

  readonly viewMonthLabel = computed(
    () => `${MONTH_NAMES[this._viewMonth()]} ${this._viewYear()}`,
  );

  readonly yearsLabel = computed(() => {
    const start = this.yearsStart();
    return `${start}–${start + 11}`;
  });

  readonly yearsGrid = computed(() => {
    const start = this.yearsStart();
    return Array.from({ length: 12 }, (_, i) => start + i);
  });

  readonly calendarDays = computed(() =>
    buildCalendarDays(this._viewYear(), this._viewMonth()),
  );

  private yearsStart(): number {
    const base = this._yearsBase();
    return base - (base % 12);
  }

  isViewYear(year: number): boolean {
    return this._viewYear() === year;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent) {
    if (
      this.open() &&
      !this.elRef.nativeElement.contains(event.target) &&
      !this.portal.contains(event.target)
    ) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.open()) {
      this.updatePosition();
    }
  }

  ngOnInit(): void {
    this.document.addEventListener('scroll', this.onScrollCapture, true);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('scroll', this.onScrollCapture, true);
    this.portal.detach();
  }

  toggle(): void {
    if (!this.dropdownTpl) {
      return;
    }

    if (this.open()) {
      this.close();
      return;
    }

    const from = fromIso(this.from());
    const to = fromIso(this.to());
    this._draftStart.set(from);
    this._draftEnd.set(to);
    this._pickingEnd.set(!from || !to);
    this._hoverDate.set(null);

    const anchor = from ?? to ?? new Date();
    this._viewYear.set(anchor.getFullYear());
    this._viewMonth.set(anchor.getMonth());
    this._yearsBase.set(anchor.getFullYear());

    this.open.set(true);
    this.portal.attach(this.dropdownTpl);
    this.updatePosition();
  }

  prevMonth(): void {
    if (this._viewMonth() === 0) {
      this._viewMonth.set(11);
      this._viewYear.update((year) => year - 1);
    } else {
      this._viewMonth.update((month) => month - 1);
    }
    this._yearsBase.set(this._viewYear());
  }

  nextMonth(): void {
    if (this._viewMonth() === 11) {
      this._viewMonth.set(0);
      this._viewYear.update((year) => year + 1);
    } else {
      this._viewMonth.update((month) => month + 1);
    }
    this._yearsBase.set(this._viewYear());
  }

  toggleYears(): void {
    this.mode.update((current) => (current === 'days' ? 'years' : 'days'));
    if (this.mode() === 'years') {
      this._yearsBase.set(this._viewYear());
    }
    if (this.open()) {
      this.updatePosition();
    }
  }

  prevYears(): void {
    this._yearsBase.update((year) => year - 12);
  }

  nextYears(): void {
    this._yearsBase.update((year) => year + 12);
  }

  selectYear(year: number): void {
    this._viewYear.set(year);
    this._yearsBase.set(year);
    this.mode.set('days');
  }

  selectDay(date: Date): void {
    const draftStart = this._draftStart();
    if (!this._pickingEnd() || !draftStart) {
      this._draftStart.set(startOfDay(date));
      this._draftEnd.set(null);
      this._pickingEnd.set(true);
      this._hoverDate.set(null);
      return;
    }

    let from = startOfDay(draftStart);
    let to = startOfDay(date);

    if (to.getTime() < from.getTime()) {
      [from, to] = [to, from];
    }

    this._draftStart.set(from);
    this._draftEnd.set(to);
    this._pickingEnd.set(false);
    this._hoverDate.set(null);
    this.close();
    this.rangeChange.emit({ from: toIso(from), to: toIso(to) });
  }

  onDayHover(date: Date): void {
    if (this._pickingEnd() && this._draftStart()) {
      this._hoverDate.set(startOfDay(date));
    }
  }

  onDayLeave(): void {
    this._hoverDate.set(null);
  }

  isRangeStart(date: Date): boolean {
    const start = this._draftStart();
    return start !== null && isSameDay(date, start);
  }

  isRangeEnd(date: Date): boolean {
    const end = this._draftEnd() ?? this._hoverDate();
    const start = this._draftStart();
    if (!start || !end) {
      return false;
    }

    const draftEnd = this._draftEnd();
    if (draftEnd) {
      return isSameDay(date, draftEnd);
    }

    return isSameDay(date, end);
  }

  isInRange(date: Date): boolean {
    const start = this._draftStart();
    const end = this._draftEnd() ?? this._hoverDate();
    if (!start || !end) {
      return false;
    }

    const time = startOfDay(date).getTime();
    const fromTime = startOfDay(start).getTime();
    const toTime = startOfDay(end).getTime();
    const min = Math.min(fromTime, toTime);
    const max = Math.max(fromTime, toTime);
    return time > min && time < max;
  }

  isSelected(date: Date): boolean {
    return this.isRangeStart(date) || this.isRangeEnd(date);
  }

  dayAria(date: Date): string {
    return `${date.getDate()}. ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }

  private close(): void {
    this.open.set(false);
    this.mode.set('days');
    this._pickingEnd.set(false);
    this._hoverDate.set(null);
    this.portal.detach();
  }

  private updatePosition(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.drp-trigger',
    ) as HTMLElement | null;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward =
      spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

    if (openUpward) {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(window.innerHeight - rect.top + DROPDOWN_GAP);
    } else {
      this.dropdownBottom.set(null);
      this.dropdownTop.set(rect.bottom + DROPDOWN_GAP);
    }

    let left = rect.left;
    if (left + DROPDOWN_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }
    this.dropdownLeft.set(left);
  }
}
